import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './EquipmentCategories.css';

import agriLoanBanner from '../assets/agri_loan_banner.png';
import agriLoanSlide2 from '../assets/agri_loan_slide2.png';
import agriLoanSlide3 from '../assets/agri_loan_slide3.png';

const EquipmentCategories = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [dynamicBanners, setDynamicBanners] = useState(null);

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const API_URL = envApiUrl || '/api';

  const slidesToRender = React.useMemo(() => {
    if (dynamicBanners && dynamicBanners.isActive && Array.isArray(dynamicBanners.slides) && dynamicBanners.slides.length > 0) {
      const validSlides = dynamicBanners.slides.filter(s => s.image && s.image.trim() !== '');
      if (validSlides.length > 0) {
        return validSlides.map(s => {
          let img = s.image;
          if (img.startsWith('uploads/') || img.startsWith('/uploads/')) {
            const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.') || window.location.hostname.startsWith('10.') || window.location.port !== '';
            let base = isLocalHost ? 'http://localhost:5000' : API_URL.replace('/api', '');
            if (!base || base.startsWith('/')) {
              base = `${window.location.protocol}//${window.location.hostname}:5000`;
            }
            img = `${base}${img.startsWith('/') ? '' : '/'}${img}`;
          }
          return { image: img, heading: s.heading, link: s.link || '/services/loan' };
        });
      }
    }
    // Fallback to static slides
    return [
      { image: agriLoanBanner, heading: t('categories.loan_title', 'Are you interested in getting a loan?'), link: '/services/loan' },
      { image: agriLoanSlide2, heading: t('categories.loan_title', 'Are you interested in getting a loan?'), link: '/services/loan' },
      { image: agriLoanSlide3, heading: t('categories.loan_title', 'Are you interested in getting a loan?'), link: '/services/loan' }
    ];
  }, [dynamicBanners, t, API_URL]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % slidesToRender.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slidesToRender]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes, cmsRes] = await Promise.all([
          fetch(`${API_URL}/products?all=true&limit=1000`, {
            headers: {
            }
          }),
          fetch(`${API_URL}/categories`, {
            headers: {
            }
          }),
          fetch(`${API_URL}/home-cms`, {
            headers: {
            }
          })
        ]);

        const prodJSON = await prodRes.json();
        const catJSON = await catRes.json();
        const cmsJSON = await cmsRes.json();

        const products = prodJSON.success && Array.isArray(prodJSON.data) ? prodJSON.data : [];
        const fetchedCategories = catJSON.success && Array.isArray(catJSON.data) ? catJSON.data : [];
        
        if (cmsJSON.success && cmsJSON.data && cmsJSON.data.loanBanners) {
          setDynamicBanners(cmsJSON.data.loanBanners);
        }

        let sortedCats = fetchedCategories.map(cat => {
          const count = cat.productCount !== undefined ? cat.productCount : products.filter(p => (p.category?._id || p.category) === cat._id).length;
          return { ...cat, productCount: count };
        }).filter(cat => cat.productCount > 0);

        sortedCats.sort((a, b) => b.productCount - a.productCount);

        setCategories(sortedCats);
      } catch (err) {
        console.error('Error fetching data for EquipmentCategories:', err);
      }
    };
    fetchData();
  }, [API_URL]);

  const getImage = (cat) => {
    const imgName = cat.image;
    if (imgName && (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:'))) {
      if (imgName.includes('ik.imagekit.io')) {
        const hasParams = imgName.includes('?');
        return imgName + (hasParams ? '&' : '?') + 'tr=w-500,q-80,f-auto';
      }
      return imgName;
    }
    if (imgName) {
      const cleanImgName = (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) ? imgName : `/uploads/${imgName}`;
      const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.') || window.location.hostname.startsWith('10.') || window.location.port !== '';
      let backendBase = isLocalHost ? 'http://localhost:5000' : API_URL.replace('/api', '');
      if (!backendBase || backendBase.startsWith('/')) {
        backendBase = `${window.location.protocol}//${window.location.hostname}:5000`;
      }
      return `${backendBase}${cleanImgName.startsWith('/') ? '' : '/'}${cleanImgName}`;
    }

    return "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSczMDAnIGhlaWdodD0nMzAwJyB2aWV3Qm94PScwIDAgMzAwIDMwMCc+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsbD0nI2YxZjVmOScvPjx0ZXh0IHg9JzUwJScgeT0nNTAlJyBkb21pbmFudC1iYXNlbGluZT0nbWlkZGxlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LWZhbWlseT0tc2Fucy1zZXJpZicgZm9udC1zaXplPSczMCcgZmlsbD0nIzk0YTNiOCc+Tm8gSW1hZ2UgQXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
  };

  const handleCategoryClick = (cat) => {
    navigate(`/category/${cat.slug}`);
  };

  return (
    <section className="equipment-categories">
      <div className="container">
        {/* Simple Top Title */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h2 className="section-title" style={{ fontSize: '38px', fontWeight: '800', color: '#2c3e50', margin: 0 }}>
            {t('categories.header_top', 'Categories')}
          </h2>
        </div>

        <div className="categories-grid">
          {categories.slice(0, 8).map((cat, index) => (
            <div 
              key={cat._id || index} 
              className="category-card"
              onClick={() => handleCategoryClick(cat)}
            >
              <div className="category-img-wrap">
                 <img 
                   src={getImage(cat)} 
                   alt={cat.name} 
                   onError={(e) => {
                     e.target.src = 'https://images.unsplash.com/photo-1592982537447-7440770cbfc9?auto=format&fit=crop&w=400&q=80';
                   }}
                 />
              </div>
              <div className="category-info">
                <h3 className="category-name">
                  {i18n.language === 'hi' && cat.hindi ? cat.hindi : cat.name}
                </h3>
                <span className="category-link">View All &rarr;</span>
              </div>
            </div>
          ))}
        </div>

        <div className="actions">
          <button className="btn-all-products" onClick={() => navigate('/products')}>
            {t('categories.view_all', 'View All Products')}
          </button>
        </div>

        {/* Separator and Descriptive Header Moved Here */}
        <div style={{ margin: '80px 0 40px', textAlign: 'center' }}>
          <div className="section-header" style={{ marginBottom: 0 }}>
            <span className="badge-tag green">{t('categories.badge', 'Equipment')}</span>
            <h2 className="section-title">{t('categories.title', 'Browse by Category')}</h2>
            <p className="section-subtitle" style={{ margin: '15px auto 0' }}>
              {t('categories.subtitle', 'Find the right equipment for every stage of your farming journey')}
            </p>
          </div>
        </div>

        {/* Loan Banner */}
        <div className="loan-banner-container" onClick={() => navigate('/services/loan')}>
          {slidesToRender.map((slide, idx) => (
            <div 
              key={idx}
              className="loan-banner-slide"
              style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.4)), url(${slide.image})`,
                opacity: currentBgIndex === idx ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 1
              }}
            />
          ))}
          <div className="loan-banner-content" style={{ position: 'relative', zIndex: 2 }}>
            <h3 className="loan-banner-title">
              {slidesToRender[currentBgIndex]?.heading || t('categories.loan_title', 'Are you interested in getting a loan?')}
            </h3>
            <button className="btn-loan-click" onClick={(e) => { e.stopPropagation(); navigate('/services/loan'); }}>
              {t('categories.loan_click', 'CLICK HERE')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EquipmentCategories;


